import React, { useEffect, useMemo, useState } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { getUserDevicesAndLocationList } from '../services/personelService';
import { getAllUsers } from '../services/userService';

const IZMIR_CENTER = [38.4237, 27.1428];

// Haritada ve personel seçiminde gösterilmeyen kullanıcılar: 69 (Ata Bülbül) ve
// 343 (Users.IsDeleted=1 olan "Ata Deneme123" test hesabı). GetUserDevicesAndLocationList
// yalnızca e-posta kapsamına bakıp silinmiş kullanıcıyı da döndürdüğü için ayıklama burada.
const HIDDEN_USER_IDS = [69, 343];

const todayString = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('tr-TR');
};

const parseCoordinates = (geoLocation) => {
  if (!geoLocation) return null;
  const [lat, lng] = String(geoLocation).split(',').map(Number);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
};

const batteryLevel = (percentage) => {
  if (percentage < 20) return 'low';
  if (percentage < 50) return 'medium';
  return 'high';
};

const BATTERY_COLORS = { low: '#c62828', medium: '#e65100', high: '#18964e' };

// Fingerprint biçimi: marka/ürün/cihaz:android-sürümü/build/...
const describeDevice = (devicesId) => {
  if (!devicesId) return '-';
  const [brand, , deviceAndVersion = ''] = devicesId.split('/');
  const [device, version] = deviceAndVersion.split(':');
  return [brand, device, version && `Android ${version}`].filter(Boolean).join(' · ') || devicesId;
};

const MapController = ({ positions }) => {
  const map = useMap();

  useEffect(() => {
    if (positions.length === 1) {
      map.setView(positions[0], 15);
    } else if (positions.length > 1) {
      map.fitBounds(positions, { padding: [30, 30] });
    }
  }, [map, positions]);

  return null;
};

const PersonelKonumBatarya = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [baslangic, setBaslangic] = useState(todayString());
  const [bitis, setBitis] = useState(todayString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAllUsers()
      .then(setUsers)
      .catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedUsers, baslangic, bitis]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserDevicesAndLocationList({
        userids: selectedUsers.length ? selectedUsers.join('#') : undefined,
        baslangic: baslangic ? `${baslangic}T00:00:00` : undefined,
        bitis: bitis ? `${bitis}T23:59:59` : undefined,
      });
      setRows(data);
    } catch (err) {
      console.error('Konum/batarya verisi çekme hatası:', err);
      setError(err.message || 'Veri yükleme hatası oluştu');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const usersById = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, `${u.name} ${u.surname}`])),
    [users]
  );

  const records = useMemo(
    () =>
      rows
        .filter((row) => !HIDDEN_USER_IDS.includes(row.userId))
        .map((row) => ({
          ...row,
          personel: usersById[row.userId] || `#${row.userId}`,
          coordinates: parseCoordinates(row.geoLocation),
          dateValue: row.date ? new Date(row.date).getTime() : 0,
        })),
    [rows, usersById]
  );

  const latestByUser = useMemo(() => {
    const latest = {};
    records.forEach((row) => {
      if (!row.coordinates) return;
      const current = latest[row.userId];
      if (!current || row.dateValue > current.dateValue) latest[row.userId] = row;
    });
    return Object.values(latest);
  }, [records]);

  const positions = useMemo(() => latestByUser.map((row) => row.coordinates), [latestByUser]);

  // Gizlenen personel seçim listesinden de çıkarılır; aksi halde seçilebilir ama
  // harita boş görünürdü.
  const userOptions = users
    .filter((user) => !HIDDEN_USER_IDS.includes(user.id))
    .map((user) => ({
      label: `${user.name} ${user.surname}`,
      value: user.id,
    }));

  return (
    <div className="people-container gunluk-is-takip-container personel-konum-batarya-container">
      <h1 className="page-title">Personel Konum ve Batarya Durumları</h1>

      <div className="search-container">
        <div className="search-inputs">
          <div className="search-input-group">
            <MultiSelect
              value={selectedUsers}
              options={userOptions}
              onChange={(e) => setSelectedUsers(e.value)}
              optionLabel="label"
              placeholder="Personel Seçiniz"
              maxSelectedLabels={3}
              selectedItemsLabel="{0} kişi seçildi"
              className="personel-select"
              filter
              filterPlaceholder="Arama Yap"
              showClear
            />
            <label className="date-label">
              Başlangıç
              <input
                type="date"
                value={baslangic}
                max={bitis || undefined}
                onChange={(e) => setBaslangic(e.target.value)}
                className="search-input date-input"
              />
            </label>
            <label className="date-label">
              Bitiş
              <input
                type="date"
                value={bitis}
                min={baslangic || undefined}
                onChange={(e) => setBitis(e.target.value)}
                className="search-input date-input"
              />
            </label>
          </div>
          <div className="search-input-group">
            <button onClick={fetchData} className="download-excel-button" disabled={loading}>
              Yenile
              <i className="pi pi-refresh" />
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="location-map">
        {loading && <div className="map-loading">Yükleniyor...</div>}
        <MapContainer center={IZMIR_CENTER} zoom={11} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController positions={positions} />
          {latestByUser.map((row) => {
            const level = batteryLevel(row.batteryPercentage);
            const device = describeDevice(row.devicesId);
            return (
              <CircleMarker
                key={row.userId}
                center={row.coordinates}
                radius={9}
                pathOptions={{ color: '#ffffff', weight: 2, fillColor: BATTERY_COLORS[level], fillOpacity: 0.9 }}
              >
                <Popup className="location-popup">
                  <div className="popup-title">{row.personel}</div>
                  <dl>
                    <dt>Son Konum</dt>
                    <dd>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${row.coordinates.join(',')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="location-link"
                      >
                        <i className="pi pi-map-marker" /> {row.geoLocation}
                      </a>
                    </dd>
                    <dt>Zaman</dt>
                    <dd>{formatDateTime(row.date)}</dd>
                    <dt>Batarya</dt>
                    <dd>
                      <span className={`battery-badge battery-${level}`}>%{row.batteryPercentage}</span>
                    </dd>
                    <dt>Cihaz</dt>
                    <dd title={row.devicesId}>{device}</dd>
                    {row.details && (
                      <>
                        <dt>Detay</dt>
                        <dd>{row.details}</dd>
                      </>
                    )}
                  </dl>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
        {!loading && !error && latestByUser.length === 0 && (
          <div className="map-legend">Seçili aralıkta konum kaydı bulunamadı.</div>
        )}
      </div>
    </div>
  );
};

export default PersonelKonumBatarya;
